import React from 'react';

import { makeStyles } from '@material-ui/styles';
import { Theme, Box } from '@material-ui/core';
import MuiTextField, { OutlinedTextFieldProps } from '@material-ui/core/TextField';

import InfoIconTooltip from './InfoIconTooltip';

// The TextField will alawys be an outlined component so omit variant prop
interface WithoutVariant extends Omit<OutlinedTextFieldProps, 'variant'> {
    readonly tooltip?: string;
    readonly errorMessage?: string;
}

function TextField({
    label,
    tooltip,
    placeholder,
    InputProps,
    InputLabelProps,
    ...otherProps
}: WithoutVariant): JSX.Element {
    const classes = useStyles();
    return (
        <MuiTextField
            variant="outlined"
            InputProps={{
                ...InputProps,
                notched: false,
                classes: {
                    adornedEnd: classes.adornedEnd,
                    input: classes.input,
                    multiline: classes.multiline,
                    notchedOutline: classes.notchedOutline,
                    focused: classes.focused,
                    inputMarginDense: classes.inputMarginDense,
                    ...(InputProps?.classes ?? {})
                }
            }}
            InputLabelProps={{
                ...InputLabelProps,
                shrink: true,
                classes: {
                    focused: classes.labelFocused,
                    outlined: classes.labelOutlined,
                    ...(InputLabelProps?.classes ?? {})
                }
            }}
            FormHelperTextProps={{
                classes: {
                    root: classes.formHelper
                }
            }}
            label={
                tooltip ? (
                    <Box display="flex" alignItems="center" gridColumnGap={5}>
                        {label}
                        <InfoIconTooltip
                            title={tooltip}
                            IconProps={{ fontSize: 'small' }}
                        />
                    </Box>
                ) : (
                    label
                )
            }
            helperText={
                otherProps.errorMessage
                    ? otherProps.errorMessage
                    : otherProps.helperText
                    ? otherProps.helperText
                    : ''
            }
            placeholder={placeholder ?? (label as string)}
            {...otherProps}
            error={Boolean(otherProps?.errorMessage)}
        />
    );
}

TextField.defaultProps = {
    tooltip: ''
};

const useStyles = makeStyles((theme: Theme) => ({
    adornedEnd: {
        paddingRight: '0 !important'
    },
    notchedOutline: {
        borderColor: theme.palette.background.border
    },
    focused: {
        '& fieldset': {
            borderColor: `${theme.palette.secondary.main} !important`,
            color: `${theme.palette.secondary.main} !important` // Maybe not necessary?
        }
    },
    multiline: {
        padding: 0 // May need to add inputMultiline with o padding and set this to 10 15 (same as input)
    },
    input: {
        padding: '10px 15px' // Default 18.5px 14px
    },
    inputMarginDense: {
        padding: 5
    },
    labelFocused: {
        color: `${theme.palette.primary.contrastText} !important`
    },
    labelOutlined: {
        '&.MuiInputLabel-shrink': {
            transform: 'initial',
            transformOrigin: 'initial',
            position: 'initial',
            pointerEvents: 'auto'
        }
    },
    formHelper: {
        marginLeft: 0
    }
}));

export default TextField;
